import React, { useState } from 'react'
import {
  Container,
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import { makeStyles } from '@material-ui/core/styles'
import LayoutForm from '../3modules/Layouts/LayoutForm'
import useLayoutEditor from '../../services/Layouts/useLayoutEditor'
import TabPreview from './TabPreview'
import { useLocation, useParams } from 'react-router-dom'
import { getCardsWithoutFields } from '../3modules/Layouts/layoutValidation'

const useStyles = makeStyles((theme) => ({
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  previewContainer: {
    marginTop: theme.spacing(4),
    border: '1px solid #ccc',
    padding: theme.spacing(2),
    borderRadius: 4,
  },
}))

export const prepareLayoutForSave = (layout) => ({
  ...layout,
  archived_at: layout.archived_at ?? null,
  cards: (layout.cards || []).map(card => ({
    ...card,
    description: card.description?.trim()
      ? card.description
      : 'Card description',
    items: (card.items || []).map(item => {
      const { display_label, display_field_type, ...savedItem } = item
      return savedItem
    })
  }))
})

const LayoutEditor = () => {
  const classes = useStyles()
  const { layoutId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const urlObjectType = queryParams.get('objectType') || 'asset';

  const {
    layout,
    setLayout,
    isLoading,
    saveFeedback,
    setSaveFeedback,
    handleSave: originalHandleSave,
    handleCancel,
    addCard,
    deleteCard,
    moveCard,
    addField,
    deleteField,
    moveField,
    industries,
    objectType,
    isDefaultLayout,
  } = useLayoutEditor(layoutId, urlObjectType)

  const [previewMode, setPreviewMode] = useState(false)
  const [emptyCardConfirmationOpen, setEmptyCardConfirmationOpen] = useState(false)

  if (isLoading || !layout) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 50 }}>
        <CircularProgress />
      </div>
    )
  }

  const saveLayout = () => originalHandleSave(prepareLayoutForSave(layout))

  const emptyCardCount = getCardsWithoutFields(layout.cards).length

  const handleSave = () => {
    if (emptyCardCount > 0) {
      setEmptyCardConfirmationOpen(true)
      return
    }

    saveLayout()
  }

  const handleSaveWithEmptyCards = () => {
    setEmptyCardConfirmationOpen(false)
    saveLayout()
  }

  return (
    <Container maxWidth='lg'>
      <h1>{layoutId ? 'Edit Layout' : 'Create New Layout'}</h1>
      <LayoutForm
        layout={layout}
        setLayout={setLayout}
        addCard={addCard}
        deleteCard={deleteCard}
        moveCard={moveCard}
        addField={addField}
        deleteField={deleteField}
        moveField={moveField}
        industries={industries}
        objectType={objectType}
        isDefaultLayout={isDefaultLayout}
      />
      {saveFeedback?.isOpen && saveFeedback.severity === 'error' && (
        <Alert
          severity='error'
          onClose={() => setSaveFeedback({
            ...saveFeedback,
            isOpen: false
          })}
          sx={{ mt: 2, whiteSpace: 'pre-line' }}
        >
          {saveFeedback.message}
        </Alert>
      )}
      <div className={classes.buttonContainer}>
        <Button variant='contained' onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant='contained' color='primary' onClick={handleSave}>
          Save
        </Button>
        <Button variant='outlined' onClick={() => setPreviewMode((prev) => !prev)}>
          {previewMode ? 'Hide Preview' : 'Show Preview'}
        </Button>
      </div>

      {previewMode && (
        <div className={classes.previewContainer}>
          <h2>Layout Preview</h2>
          <TabPreview layout={layout} />
        </div>
      )}

      <Dialog
        open={emptyCardConfirmationOpen}
        onClose={() => setEmptyCardConfirmationOpen(false)}
        aria-labelledby='empty-card-confirmation-title'
        aria-describedby='empty-card-confirmation-description'
      >
        <DialogTitle id='empty-card-confirmation-title'>
          Empty {emptyCardCount === 1 ? 'card' : 'cards'} will not be saved
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='empty-card-confirmation-description'>
            This layout contains {emptyCardCount}{' '}
            {emptyCardCount === 1 ? 'card' : 'cards'} with no fields. Empty cards
            will be discarded when the layout is saved. Would you like to
            continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmptyCardConfirmationOpen(false)}>
            Go back
          </Button>
          <Button color='primary' onClick={handleSaveWithEmptyCards}>
            Continue and save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default LayoutEditor
